import {Box, Flex, Text} from '@chakra-ui/react';
import usePlannerStyles from '../planner/usePlannerStyles';
import {CREATE_SESSION_CARD_PADDING} from '../planner/CreateSessionFormSections';
import {CREATE_SESSION_SECTION_LABEL_PROPS} from '../planner/createSessionSectionLayout';
import {RECAP_CARD_BOUNDARY_PROPS} from '../common/recapButtonStyles';

// One-shot entrance for AI-generated text settling into a field (the Message
// composer's draft, the Action Taken auto-draft). Matches the rest of the
// surface's motion language — the same soft fade + few-pixel rise the detail
// body uses (`te-detail-fade-in`) and the streamed-chunk reveal in the
// conversation panel — so the generation result reads as "lands in" rather
// than popping. Honors reduced-motion like the planner's row/drawer entrances.
export const TE_GENERATED_REVEAL_SX = {
  animation: 'te-generated-reveal 420ms cubic-bezier(0.22, 1, 0.36, 1) both',
  '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
  '@keyframes te-generated-reveal': {
    from: {opacity: 0, transform: 'translateY(6px)'},
    to: {opacity: 1, transform: 'translateY(0)'},
  },
} as const;

// Card body matching the encounter detail / note panels and the New Encounter
// composer (surface + 1px border + lg radius + shared padding + shadow).
export const TeSectionCard = ({
  children,
  ...rest
}: {children: React.ReactNode} & Record<string, any>) => {
  const {surfaceBg, borderColor, cardShadow} = usePlannerStyles();
  return (
    <Box
      {...RECAP_CARD_BOUNDARY_PROPS}
      bg={surfaceBg}
      borderColor={borderColor}
      boxShadow={cardShadow}
      p={CREATE_SESSION_CARD_PADDING}
      {...rest}
    >
      {children}
    </Box>
  );
};

// One labelled form row — label on the left, control on the right, stacking on
// mobile — exactly like the New Encounter composer's sections. Rows after the
// first get a hairline top divider so the card reads as a single grouped form.
export const DetailFormRow = ({
  label,
  htmlFor,
  isFirst = false,
  alignTop = false,
  isRequired = false,
  children,
}: {
  label: string;
  htmlFor?: string;
  isFirst?: boolean;
  alignTop?: boolean;
  isRequired?: boolean;
  children: React.ReactNode;
}) => {
  const {borderColor, textColor} = usePlannerStyles();
  return (
    <Box
      as="section"
      borderTopWidth={isFirst ? 0 : '1px'}
      borderColor={borderColor}
      pt={isFirst ? 0 : 4}
    >
      <Flex
        gap={{base: 2, md: 3}}
        align={{base: 'stretch', md: alignTop ? 'flex-start' : 'center'}}
        direction={{base: 'column', md: 'row'}}
      >
        <Text
          as={htmlFor ? 'label' : 'span'}
          color={textColor}
          {...CREATE_SESSION_SECTION_LABEL_PROPS}
          pt={alignTop ? {md: 2.5} : undefined}
          {...((htmlFor ? {htmlFor} : {}) as any)}
        >
          {label}
          {isRequired ? (
            <Text as="span" color="red.500" aria-hidden="true" ml={1}>
              *
            </Text>
          ) : null}
        </Text>
        <Box flex="1" minW={0}>
          {children}
        </Box>
      </Flex>
    </Box>
  );
};
